/**
 * Application composition, authenticated route selection, initial planner hydration, and
 * restoration of active immersion panels. Task refreshes use revision/user snapshots; immersion
 * restoration ignores sessions closed locally while the request was pending.
 *
 * @module apps/web/src/App
 */

import { memo, useEffect, useEffectEvent } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/toaster';
import { Layout } from '@/components/layout/Layout';
import { KeepAliveRoutes } from '@/components/layout/KeepAliveRoutes';
import { RouteDataProvider } from '@/stores/routeData';
import { DayView } from '@/components/calendar/DayView';
import { WeekView } from '@/components/calendar/WeekView';
import { MonthView } from '@/components/calendar/MonthView';
import { KanbanView } from '@/components/kanban/KanbanView';
import { TableView } from '@/components/table/TableView';
import { ScissorPage } from '@/pages/ScissorPage';
import { Settings } from '@/pages/Settings';
import { Analytics } from '@/pages/Analytics';
import { StatePage } from '@/pages/StatePage';
import { ValuesPage } from '@/pages/ValuesPage';
import { WorkspacePage } from '@/pages/WorkspacePage';
import { UnscheduledPage } from '@/pages/UnscheduledPage';
import { TeamsPage } from '@/pages/TeamsPage';
import { OrganizationPage } from '@/pages/OrganizationPage';
import { HelpPage } from '@/pages/HelpPage';
import { InvitePage } from '@/pages/InvitePage';
import { FluxAgentPage } from '@/pages/FluxAgentPage';
import { EditorToolsPage } from '@/pages/EditorToolsPage';
import { EditorAuthorizePage } from '@/pages/EditorAuthorizePage';
import { CalendarConnected } from '@/pages/CalendarConnected';
import { AuthPage } from '@/pages/AuthPage';
import { Onboarding } from '@/pages/Onboarding';
import { CommandPalette } from '@/components/command/CommandPalette';
import { ImmersionMode } from '@/components/immersion/ImmersionMode';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { useWebSocket } from '@/hooks/useWebSocket';
import { useTasksStore, captureTaskSnapshot, isTaskSnapshotCurrent } from '@/stores/tasksStore';
import { useScheduleStore } from '@/stores/scheduleStore';
import { useUserStore } from '@/stores/userStore';
import { useWorkspaceStore } from '@/stores/workspaceStore';
import { api } from '@/lib/api';
import { rolloverCalendarLocally } from '@/lib/calendarRollover';
import { getSchedulingSessionEpoch } from '@/lib/browserScheduleFlow';
import { captureCalendarReadFence } from '@/lib/browserGhostBlocks';
import { useEnergyModelTraining } from '@/lib/energyModelHook';
import { taskToUI } from '@fluxability/types';
import type { GhostBlocksData, CalendarEventWire } from '@fluxability/types';
import { Button } from '@/components/ui/button';
import { TimerReset } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Every authenticated route. A module constant, so `KeepAliveRoutes` can skip re-rendering the
 * hidden screens it retains when `AppContent` re-renders.
 */
const appRoutes = (
  <>
    <Route path="/" element={<Navigate to="/week" replace />} />
    <Route path="/day" element={<DayView />} />
    <Route path="/week" element={<WeekView />} />
    <Route path="/month" element={<MonthView />} />
    <Route path="/kanban" element={<KanbanView />} />
    <Route path="/table" element={<TableView />} />
    <Route path="/scissor" element={<ScissorPage />} />
    <Route path="/analytics" element={<Analytics />} />
    <Route path="/state" element={<StatePage />} />
    <Route path="/workspace" element={<WorkspacePage />} />
    <Route path="/values" element={<ValuesPage />} />
    <Route path="/teams" element={<TeamsPage />} />
    <Route path="/teams/:teamId" element={<TeamsPage />} />
    <Route path="/teams/:teamId/:section" element={<TeamsPage />} />
    <Route path="/organization" element={<OrganizationPage />} />
    <Route path="/unscheduled" element={<UnscheduledPage />} />
    <Route path="/settings" element={<Settings />} />
    <Route path="/agent" element={<FluxAgentPage />} />
    <Route path="/editor-tools" element={<EditorToolsPage />} />
    <Route path="/editor-tools/authorize" element={<EditorAuthorizePage />} />
    <Route path="/help" element={<HelpPage />} />
    <Route path="/invite" element={<InvitePage />} />
    <Route path="/calendar/connected" element={<CalendarConnected />} />
  </>
);

/** Capture the private planner owner before a read, including logout intent before token removal. */
function plannerOwnerFence(): () => boolean {
  const owner = useUserStore.getState().userId;
  const token = localStorage.getItem('flexiplanner-session-token');
  const epoch = getSchedulingSessionEpoch();
  return () =>
    Boolean(owner) &&
    owner === useUserStore.getState().userId &&
    token === localStorage.getItem('flexiplanner-session-token') &&
    epoch === getSchedulingSessionEpoch();
}

/** Compose authenticated routes and global overlays, hydrate planner stores after onboarding, and restore active work panels. Refresh snapshots protect newer activity edits; hiding or minimizing a panel does not itself end its server session. */
function AppContent() {
  const { isLoading, isAuthenticated } = useAuth();
  const onboardingCompleted = useUserStore(s => s.onboardingCompleted);
  const setTasks = useTasksStore(s => s.setTasks);
  const setBlocks = useScheduleStore(s => s.setBlocks);
  const setGhostBlocks = useScheduleStore(s => s.setGhostBlocks);
  const setCalendarEvents = useScheduleStore(s => s.setCalendarEvents);
  const toggleCommandPalette = useScheduleStore(s => s.toggleCommandPalette);
  const startImmersionMode = useScheduleStore(s => s.startImmersionMode);
  const loadWorkspace = useWorkspaceStore(s => s.loadWorkspace);

  // Train the personal energy model at idle on capable devices; never blocks interaction.
  useEnergyModelTraining(
    useUserStore(s => s.userId),
    isAuthenticated && onboardingCompleted
  );

  // Initialize WebSocket connection
  useWebSocket();

  // Initialize keyboard shortcuts
  useKeyboardShortcuts({
    'cmd+k': () => toggleCommandPalette(),
    'ctrl+k': () => toggleCommandPalette(),
  });

  // The loaders read the latest stores when they run; only sign-in and onboarding start them.
  const startCurrentPlanLoad = useEffectEvent(() => loadCurrentPlan());
  const startActiveImmersionLoad = useEffectEvent(() => loadActiveImmersion());

  // Load tasks and schedule on mount
  useEffect(() => {
    if (isAuthenticated && onboardingCompleted) {
      startCurrentPlanLoad();
      loadWorkspace();
      startActiveImmersionLoad();
    }
  }, [isAuthenticated, onboardingCompleted, loadWorkspace]);

  /** Share missed-work rollover with calendar views, then hydrate activities before suggestions so the startup read cannot invalidate its own local preview. */
  const loadCurrentPlan = async () => {
    const currentOwner = plannerOwnerFence();
    try {
      await rolloverCalendarLocally();
    } catch (error) {
      console.error('Failed to replan missed activities:', error);
    }

    if (!currentOwner()) return;
    await loadTasks();
    if (currentOwner()) await loadSchedule();
  };

  /** Restore server-reported active sessions only for the captured user, tracking local panel closures during the read so a late response cannot reopen them. Always unsubscribe from the temporary store observer. */
  const loadActiveImmersion = async () => {
    // Restoration can arrive after the user cancels a visible session. Remember
    // closures while this read is in flight so an old response cannot reopen it.
    const closedWhileLoading = new Set<string>();
    const userId = useUserStore.getState().userId;
    const unsubscribe = useScheduleStore.subscribe((state, previous) => {
      for (const id of previous.activeImmersionTaskIds) {
        if (!state.activeImmersionTaskIds.includes(id)) closedWhileLoading.add(id);
      }
    });
    try {
      const response = await api.training.getActiveImmersions();
      if (
        response.success &&
        Array.isArray(response.data) &&
        userId === useUserStore.getState().userId
      ) {
        response.data.forEach(session => {
          if (session.task_id && !closedWhileLoading.has(session.task_id))
            startImmersionMode(session.task_id);
        });
      }
    } catch (error) {
      console.error('Failed to load active immersion:', error);
    } finally {
      unsubscribe();
    }
  };

  /** Capture identity plus mutation revision before fetching; pass that snapshot to setTasks so edits or account changes made during the read win over stale data. */
  const loadTasks = async () => {
    const currentOwner = plannerOwnerFence();
    try {
      const snapshot = captureTaskSnapshot();
      const response = await api.tasks.list();
      if (currentOwner() && response.success && response.data) {
        // Handle both format variations just in case
        const tasksList = Array.isArray(response.data) ? response.data : response.data?.tasks;
        if (tasksList) {
          const uiTasks = tasksList.map(taskToUI);
          setTasks(uiTasks, snapshot);
        }
      }
    } catch (error) {
      console.error('Failed to load activities:', error);
    }
  };

  /** Apply locally computed suggestions only for the captured session and activity revision. */
  const loadSchedule = async () => {
    const currentOwner = captureCalendarReadFence();
    const snapshot = captureTaskSnapshot();
    const week = new Date(useScheduleStore.getState().currentDate);
    week.setDate(week.getDate() - ((week.getDay() + 6) % 7));
    week.setHours(0, 0, 0, 0);
    try {
      const response = await api.schedule.getGhostBlocks(week.toISOString());
      if (currentOwner() && isTaskSnapshotCurrent(snapshot) && response.success && response.data) {
        setBlocks(normalizeScheduleBlocks(response.data));
        setGhostBlocks(normalizeGhostBlocks(response.data));
        // Set calendar events from the response
        if (response.data.gcal_events) {
          setCalendarEvents(normalizeCalendarEvents(response.data.gcal_events));
        }
      }
    } catch (error) {
      console.error('Failed to load schedule:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-dvh items-center justify-center">
        <div className="text-center">
          <div className="border-primary mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2"></div>
          <p className="text-muted-foreground">Loading Fluxability...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        <AuthPage />
        <Toaster />
      </>
    );
  }

  if (!onboardingCompleted) {
    return (
      <>
        <Onboarding />
        <Toaster />
      </>
    );
  }

  // The routes, the palette and the session panels each subscribe to their own state, so opening
  // the palette or minimizing a session does not re-render the routed screen.
  return (
    <>
      <PlannerRoutes />
      <CommandPaletteHost />
      <ImmersionPanels />
      <Toaster />
    </>
  );
}

/**
 * The shell and every planner route. Memoized without props: it re-renders on navigation (through
 * the router context) and on its own store subscriptions, never because a global overlay opened.
 * `KeepAliveRoutes` keeps visited screens mounted but hidden; because `appRoutes` is a module
 * constant, those hidden screens are skipped when this component re-renders.
 */
const PlannerRoutes = memo(function PlannerRoutes() {
  return (
    <Layout>
      <RouteDataProvider>
        <KeepAliveRoutes>{appRoutes}</KeepAliveRoutes>
      </RouteDataProvider>
    </Layout>
  );
});

/** Mount the command palette with its open flag from the schedule store. Opening it re-renders only the palette. */
function CommandPaletteHost() {
  const isCommandPaletteOpen = useScheduleStore(s => s.isCommandPaletteOpen);
  const setCommandPaletteOpen = useScheduleStore(s => s.setCommandPaletteOpen);
  return <CommandPalette open={isCommandPaletteOpen} onClose={() => setCommandPaletteOpen(false)} />;
}

/**
 * Active work-session panels and the restore buttons of minimized ones. A minimized panel stays
 * mounted but hidden, so restoring it shows its running timer, pauses and notes at once instead of
 * reloading the session from the server. Hiding a panel does not end its server session.
 */
function ImmersionPanels() {
  const activeImmersionTaskIds = useScheduleStore(s => s.activeImmersionTaskIds);
  const minimizedImmersionTaskIds = useScheduleStore(s => s.minimizedImmersionTaskIds);
  const endImmersionMode = useScheduleStore(s => s.endImmersionMode);
  const minimizeImmersionMode = useScheduleStore(s => s.minimizeImmersionMode);
  const restoreImmersionMode = useScheduleStore(s => s.restoreImmersionMode);
  // Only the session records are observed. Scheduling 100 unrelated activities must not
  // re-render the session panels.
  const tasks = useTasksStore(
    useShallow(s =>
      s.tasks.filter(
        task =>
          activeImmersionTaskIds.includes(task.id) || minimizedImmersionTaskIds.includes(task.id)
      )
    )
  );

  const sessionTasks = activeImmersionTaskIds
    .map(taskId => tasks.find(t => t.id === taskId))
    .filter(task => task !== undefined);
  const visibleCount = sessionTasks.filter(task => !minimizedImmersionTaskIds.includes(task.id)).length;
  const minimizedImmersionTasks = minimizedImmersionTaskIds
    .map(taskId => tasks.find(t => t.id === taskId))
    .filter(task => task !== undefined);

  return (
    <>
      {sessionTasks.length > 0 && (
        <div
          className={cn(
            'native-scroll bg-background/78 fixed inset-0 z-50 flex-col gap-3 overflow-y-auto px-3 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-[calc(1rem+env(safe-area-inset-top))] backdrop-blur-xl md:inset-x-auto md:bottom-4 md:right-4 md:top-auto md:max-h-[calc(100dvh-2rem)] md:w-[420px] md:bg-transparent md:p-0 md:backdrop-blur-none',
            visibleCount > 0 ? 'flex' : 'hidden'
          )}
        >
          {sessionTasks.map(task => (
            <div
              key={task.id}
              className={minimizedImmersionTaskIds.includes(task.id) ? 'hidden' : 'contents'}
            >
              <ImmersionMode
                task={task}
                onMinimize={() => minimizeImmersionMode(task.id)}
                onClose={() => endImmersionMode(task.id)}
                onComplete={() => endImmersionMode(task.id)}
              />
            </div>
          ))}
        </div>
      )}
      {minimizedImmersionTasks.length > 0 && (
        <div className="fixed inset-x-3 bottom-20 z-50 flex flex-wrap gap-2 sm:inset-x-auto sm:bottom-4 sm:right-4 sm:max-w-[460px]">
          {minimizedImmersionTasks.map(task => (
            <Button
              key={task.id}
              type="button"
              variant="secondary"
              className="min-h-10 max-w-full justify-start rounded-full shadow-lg"
              onClick={() => restoreImmersionMode(task.id)}
              title={`Restore ${task.title}`}
            >
              <TimerReset className="mr-2 h-4 w-4 shrink-0" />
              <span className="truncate">Session: {task.title}</span>
            </Button>
          ))}
        </div>
      )}
    </>
  );
}

/**
 * Mount the authentication provider and planner application. Loading, sign-in, and onboarding gates precede planner routes. The inner application connects realtime updates and keyboard commands, loads tasks and schedules after rollover, and restores active sessions only for the user who started the read.
 */
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

/** Project ghost proposals and recorded work into display blocks; pause intervals stay empty. A ghost remains tentative; a work segment represents recorded activity rather than a new scheduling request. */
function normalizeScheduleBlocks(data: Pick<GhostBlocksData, 'ghost_blocks' | 'work_segments'>) {
  const ghostBlocks = (data.ghost_blocks || []).map(block => ({
    id: block.id,
    taskId: block.task_id,
    title: block.title || 'Suggested activity',
    startTime: block.proposed_start,
    endTime: block.proposed_end,
    isGhost: true,
  }));

  const workSegments = (data.work_segments || []).filter(segment => segment.segment_type !== 'pause').map(segment => ({
    id: segment.id,
    sessionId: segment.session_id,
    workSegmentIndex: segment.segment_index,
    workSegmentCount: segment.segment_count,
    taskId: segment.task_id ?? '',
    title: segment.title || 'Focused work',
    startTime: segment.start_time,
    endTime: segment.end_time,
    isGhost: false,
    priority: 3,
  }));

  return [...ghostBlocks, ...workSegments];
}

/** Project proposed start/end times and confidence/cost explanations into the tentative-block store without confirming the proposed placement. */
function normalizeGhostBlocks(data: Pick<GhostBlocksData, 'ghost_blocks' | 'work_segments'>) {
  return (data.ghost_blocks || []).map(block => ({
    id: block.id,
    taskId: block.task_id,
    proposedStart: block.proposed_start,
    proposedEnd: block.proposed_end,
    cost: block.cost,
    confidence: block.confidence,
    reason: block.reason,
  }));
}

/** Adapt calendar events into UI records. Accepts both wire spellings: camelCase from `/calendar/events` and snake_case commitments from schedule responses. Defaults commitments to immovable, parses custom attributes, and removes entries that should not appear as timed commitments. */
function normalizeCalendarEvents(events: CalendarEventWire[] = []) {
  return events
    .map(event => ({
      id: event.id,
      title: event.title || event.summary || 'Calendar event',
      start: event.start || event.start_time || '',
      end: event.end || event.end_time || '',
      isImmovable: Boolean(event.isImmovable ?? event.is_immovable ?? true),
      isFlexHold: Boolean(event.isFlexHold ?? event.is_flex_hold),
      stateProfile: event.stateProfile || event.state_profile || null,
      customAttributes: parseCustomAttributes(event.customAttributes ?? event.custom_attributes),
      allowConflict: Boolean(event.allowConflict ?? event.allow_conflict),
      seriesAllowConflict: Boolean(event.seriesAllowConflict),
      isRecurring: Boolean(event.isRecurring),
    }))
    .filter(isRenderableCalendarEvent);
}

/** Exclude date-only/all-day entries and imported Canvas or Book-Tracker activities from timed commitment rendering. Protected Fluxability holds remain visible after the timed-event check, preventing duplicate imported work on the calendar. */
function isRenderableCalendarEvent(event: {
  /** Optional event start text; absence of a time separator marks the event untimed. */
  start?: string;
  /** Optional event end text; absence of a time separator marks the event untimed. */
  end?: string;
  /** Protected free-time marker that bypasses imported-source filtering after the timed-event check. */
  isFlexHold?: boolean;
  /** Parsed provider/source metadata used to omit duplicated imported commitments. */
  customAttributes?: Record<string, unknown>;
}): boolean {
  if (isAllDayCalendarEvent(event)) return false;
  if (event.isFlexHold) return true;

  const attributes = event.customAttributes || {};
  const source = String(attributes.source || '').toLowerCase();
  return (
    source !== 'canvas' &&
    source !== 'book-tracker' &&
    !attributes.canvas &&
    !attributes.book_tracker
  );
}

/** Treat either endpoint without a time separator as all-day/untimed. This is a display-shape check, not a full timestamp validator. */
function isAllDayCalendarEvent(event: {
  /** Optional event start text; absence of a time separator marks the event untimed. */ start?: string;
  /** Optional event end text; absence of a time separator marks the event untimed. */ end?: string;
}): boolean {
  return !String(event.start || '').includes('T') || !String(event.end || '').includes('T');
}

/** Accept existing object values or JSON object strings for legacy custom attributes. Missing, malformed, or scalar values become an empty record; the function does not validate individual field meanings. */
function parseCustomAttributes(value: unknown): Record<string, unknown> {
  if (!value) return {};
  if (typeof value === 'object') return value as Record<string, unknown>;
  if (typeof value !== 'string') return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

/**
 * React application entry component; wraps all planner routes and session panels in AuthProvider.
 */
export default App;
